import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TramiteAbreviacion } from '../../models/TramiteAbreviacion';
import { UnidadOrganizacionalService } from '../../services/unidad-organizacional.service';
import { TramiteAbreviacionesService } from '../../services/tramite-abreviaciones.service';
import { UnidadOrganizacional } from '../../models/UnidadOrganizacional';
import { ScnackbarData, ToastService, SnackbarStyles } from '../toast.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { map, Observable, startWith } from 'rxjs';
import { departamentoValidator } from '../reasignar/reasignar.component';
import { AsyncPipe } from '@angular/common';
import { UtilService } from '../../services/util.service';

@Component({
    selector: 'app-dlg-update-tramite-abreviacion',
    imports: [
        MatButtonModule, MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent,
        ReactiveFormsModule,
        MatFormFieldModule, MatInputModule,
        MatProgressSpinnerModule,
        MatOptionModule,
        MatOptionModule,
        MatAutocompleteModule,
        AsyncPipe, MatInputModule
    ],
    templateUrl: './dlg-update-tramite-abreviacion.component.html',
    styleUrl: './dlg-update-tramite-abreviacion.component.scss'
})
export class DlgUpdateTramiteAbreviacionComponent {
  readonly dialogRef = inject(MatDialogRef<DlgUpdateTramiteAbreviacionComponent>)
  readonly data = inject<TramiteAbreviacion | null>(MAT_DIALOG_DATA, { optional: true })

  tramiteAbreviacion: TramiteAbreviacion | null

  fetchingUpdateTramiteAbreviacion = false
  snackbarData: ScnackbarData = {}

  directorioEmpresaConfigID = 50
  directorioEmpresa = "EMPRESA"
  departamentoAbbreviation = "DEPARTAMENTO"
  abbreviationTemplate = "{ANIO}-{NUMERO}-{TIPO}"

  updateAbbreviationForm = new FormGroup({
    codigoDepartamento: new FormControl<UnidadOrganizacional>({ id: "", nombre: "", tipo: 0 }, {
      validators: [Validators.required, departamentoValidator()]
    }),
    abreviacion: new FormControl("", {
      validators: [Validators.required]
    }),
  })

  departamentos: UnidadOrganizacional[] = []
  departamentosFiltered?: Observable<UnidadOrganizacional[]>

  utilService: UtilService = inject(UtilService);

  get isCreateMode(): boolean {
    return !this.tramiteAbreviacion?.id
  }

  constructor(
    private toast: ToastService,
    private unidadOrganizacionalService: UnidadOrganizacionalService,
    private tramiteAbreviacionesService: TramiteAbreviacionesService
  ) {
    this.tramiteAbreviacion = this.data ? structuredClone(this.data) : null
  }

  ngOnInit(): void {
    if (this.isCreateMode) {
      this.utilService.getConfigurationParameter(this.directorioEmpresaConfigID).subscribe({
        next: (response) => {
          this.directorioEmpresa = response.texto
          this.setAbbreviationTemplate()
        }
      });
    }

    this.unidadOrganizacionalService.listDepartamentos().subscribe({
      next: (response) => {
        this.departamentos = response

        if (this.tramiteAbreviacion) {
          const departamento = this.departamentos.find(depa => depa.id === this.tramiteAbreviacion!.depaID)
          if (departamento) {
            this.updateAbbreviationForm.controls.codigoDepartamento.setValue(departamento)
          }
          this.updateAbbreviationForm.controls.abreviacion.setValue(this.tramiteAbreviacion.abreviatura)
        }

        this.departamentosFiltered = this.updateAbbreviationForm.controls.codigoDepartamento.valueChanges.pipe(
          startWith(''),
          map(value => {
            const departamentoAbbreviation = (value as UnidadOrganizacional).abreviacion?.trim();
            if (this.isCreateMode && departamentoAbbreviation && departamentoAbbreviation.length > 0) {
              this.departamentoAbbreviation = departamentoAbbreviation
              this.setAbbreviationTemplate()
            }
            const nombres = typeof value === 'string' ? value : value?.nombre;
            return nombres ? this._filterDepartamento(nombres as string) : this.departamentos.slice();
          }),
        );
      }
    })
  }

  updateTramiteAbreviacionHandler() {
    const controls = this.updateAbbreviationForm.controls
    const numeracionDocumento: TramiteAbreviacion = {
      depaID: controls.codigoDepartamento.getRawValue()!.id,
      abreviatura: controls.abreviacion.getRawValue()!,
    }

    if (this.tramiteAbreviacion?.id) {
      numeracionDocumento.id = this.tramiteAbreviacion.id

      this.tramiteAbreviacionesService.patchTramiteAbreviacion(numeracionDocumento).subscribe({
        next: (response) => {
          this.snackbarData.message = "Numeración actualizada"
          this.snackbarData.style = SnackbarStyles.Success
          this.snackbarData.iconName = "check_circle"
          this.toast.show(this.snackbarData)
          this.fetchingUpdateTramiteAbreviacion = false
          this.dialogRef.close(response)
        },
        error: () => {
          this.snackbarData.message = "Error actualizando numeración"
          this.snackbarData.style = SnackbarStyles.Error
          this.snackbarData.iconName = "error"
          this.toast.show(this.snackbarData)
          this.fetchingUpdateTramiteAbreviacion = false
          this.dialogRef.close(null)
        }
      })
      return
    }

    this.tramiteAbreviacionesService.postTramiteAbreviacion(numeracionDocumento).subscribe({
      next: (response) => {
        this.snackbarData.message = "Numeración creada"
        this.snackbarData.style = SnackbarStyles.Success
        this.snackbarData.iconName = "check_circle"
        this.toast.show(this.snackbarData)
        this.fetchingUpdateTramiteAbreviacion = false
        this.dialogRef.close(response)
      },
      error: () => {
        this.snackbarData.message = "Error creando numeración"
        this.snackbarData.style = SnackbarStyles.Error
        this.snackbarData.iconName = "error"
        this.toast.show(this.snackbarData)
        this.fetchingUpdateTramiteAbreviacion = false
        this.dialogRef.close(null)
      }
    })
  }

  setAbbreviationTemplate() {
    const controls = this.updateAbbreviationForm.controls
    const departamento = controls.codigoDepartamento.getRawValue() as UnidadOrganizacional
    const abreviacion = controls.abreviacion.getRawValue() as string

    if (departamento && abreviacion !== null) {
      controls.abreviacion.setValue(`${this.directorioEmpresa}-${this.departamentoAbbreviation}-${this.abbreviationTemplate}`)
    }
  }

  displayDepartamentoFn(departamento: UnidadOrganizacional): string {
    return departamento && departamento.nombre ? departamento.nombre : '';
  }

  private _filterDepartamento(filter: string): UnidadOrganizacional[] {
    const filterValue = filter.toLowerCase();

    return this.departamentos.filter(departamento =>
      departamento.nombre.toLowerCase().includes(filterValue));
  }

  selectAllText(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
  }

}
